import * as React from 'react'

import { Toast as ToastPrimitive } from '@base-ui/react/toast'
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
} from 'lucide-react'

import { Button } from '@/components/shadcn-ui/button'
import { cn } from '@/lib/utils'
import {
  flushQueuedToasts,
  toastManager as appToastManager,
} from '~/client/src/shared/utils/toast'

function ToastProvider({ ...props }: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider {...props} />
}

function ToastPortal({ ...props }: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />
}

function ToastViewport({ className, ...props }: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      data-slot="toast-viewport"
      className={cn(
        'tw:pointer-events-none tw:fixed tw:inset-x-4 tw:bottom-4 tw:z-toast tw:mx-auto tw:w-auto tw:max-w-sm tw:outline-none tw:sm:right-4 tw:sm:left-auto tw:sm:mx-0 tw:sm:w-full',
        className,
      )}
      {...props}
    />
  )
}

function Toast({ className, ...props }: ToastPrimitive.Root.Props) {
  return (
    <ToastPrimitive.Root
      data-slot="toast"
      className={cn(
        'tw:group/toast tw:pointer-events-auto tw:absolute tw:right-0 tw:bottom-0 tw:z-[calc(1000-var(--toast-index))] tw:w-full tw:origin-bottom tw:rounded-md tw:border tw:bg-popover tw:text-popover-foreground tw:shadow-md tw:will-change-transform tw:outline-none tw:select-none tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50',
        'tw:[--gap:0.75rem] tw:[--height:var(--toast-frontmost-height,var(--toast-height))] tw:[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] tw:[--peek:0.75rem] tw:[--scale:calc(max(0,1-(var(--toast-index)*0.1)))] tw:[--shrink:calc(1-var(--scale))]',
        'tw:h-(--height) tw:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] tw:[transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_500ms,height_150ms]',
        'tw:after:absolute tw:after:top-full tw:after:left-0 tw:after:h-[calc(var(--gap)+1px)] tw:after:w-full tw:after:content-[""]',
        'tw:data-expanded:h-(--toast-height) tw:data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',
        'tw:data-limited:opacity-0 tw:data-starting-style:[transform:translateY(150%)]',
        'tw:[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]',
        'tw:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'tw:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'tw:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'tw:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        'tw:data-expanded:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'tw:data-expanded:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'tw:data-expanded:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'tw:data-expanded:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        className,
      )}
      {...props}
    />
  )
}

function ToastContent({ className, ...props }: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn(
        'tw:flex tw:h-full tw:items-start tw:gap-2 tw:overflow-hidden tw:p-4 tw:transition-opacity tw:duration-250 tw:ease-[cubic-bezier(0.22,1,0.36,1)] tw:data-behind:opacity-0 tw:data-expanded:opacity-100',
        className,
      )}
      {...props}
    />
  )
}

function ToastTitle({ className, ...props }: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn('tw:m-0 tw:text-app-dense-medium', className)}
      {...props}
    />
  )
}

function ToastDescription({
  className,
  render = <div />,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      render={render}
      className={cn(
        'tw:m-0 tw:text-app-dense tw:text-muted-foreground',
        className,
      )}
      {...props}
    />
  )
}

function ToastAction({
  className,
  render = <Button variant="default" size="xs" />,
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      render={render}
      className={cn('tw:shrink-0', className)}
      {...props}
    />
  )
}

function ToastCancel({
  className,
  render = <Button variant="secondary" size="xs" />,
  ...props
}: ToastPrimitive.Close.Props) {
  return (
    <ToastPrimitive.Close
      data-slot="toast-cancel"
      render={render}
      className={cn('tw:shrink-0', className)}
      {...props}
    />
  )
}

function ToastIcon({ type }: { type: string | undefined }) {
  let icon: React.ReactNode = null

  if (type === 'success') {
    icon = <CircleCheckIcon aria-hidden="true" />
  }

  if (type === 'info') {
    icon = <InfoIcon aria-hidden="true" />
  }

  if (type === 'warning') {
    icon = <TriangleAlertIcon aria-hidden="true" />
  }

  if (type === 'error') {
    icon = <CircleAlertIcon aria-hidden="true" />
  }

  if (!icon) {
    return null
  }

  return (
    <span
      data-slot="toast-icon"
      className="tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-4"
    >
      {icon}
    </span>
  )
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager()

  // Fragment-wrapped: React 17 typings reject an array return from a component.
  return (
    <>
      {toasts.map(toastItem => (
        <Toast key={toastItem.id} toast={toastItem}>
          <ToastContent>
            <ToastIcon type={toastItem.type} />
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:justify-center tw:gap-0.5">
              <ToastTitle />
              <ToastDescription />
            </div>
            {/* The spec pins each control into a 20px box — the line box of the
                first text row — and lets the 24px button overflow it, so a
                trailing control never makes the toast taller than its text. */}
            {toastItem.actionProps ? (
              <div className="tw:flex tw:h-5 tw:shrink-0 tw:items-center">
                <ToastAction />
              </div>
            ) : null}
            {toastItem.data?.cancel ? (
              <div className="tw:flex tw:h-5 tw:shrink-0 tw:items-center">
                <ToastCancel onClick={toastItem.data.cancel.onClick}>
                  {toastItem.data.cancel.text}
                </ToastCancel>
              </div>
            ) : null}
          </ToastContent>
        </Toast>
      ))}
    </>
  )
}

function Toaster({
  children,
  toastManager,
  viewportClassName,
  ...props
}: Omit<ToastPrimitive.Provider.Props, 'toastManager'> & {
  toastManager: NonNullable<ToastPrimitive.Provider.Props['toastManager']>
  viewportClassName?: string
}) {
  // Releases anything the façade buffered while nothing was subscribed. The
  // provider below is a descendant, and passive effects flush child-first, so by
  // now it is listening. Only the façade's own manager qualifies: a viewport
  // bound to a different manager (an isolated one in a story, say) would
  // otherwise mark the façade ready while nothing serves its queue, silently
  // disarming the dev guard.
  React.useEffect(() => {
    if (toastManager === appToastManager) {
      flushQueuedToasts()
    }
  }, [toastManager])

  return (
    <ToastProvider toastManager={toastManager} {...props}>
      {children}
      <ToastPortal>
        <ToastViewport className={viewportClassName}>
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  )
}

const createToastManager = ToastPrimitive.createToastManager
const useToastManager = ToastPrimitive.useToastManager

export {
  Toaster,
  Toast,
  ToastAction,
  ToastCancel,
  ToastContent,
  ToastDescription,
  ToastPortal,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  createToastManager,
  useToastManager,
}
